/**
 * City search component
 * Search and select cities/regions using OpenStreetMap geocoding
 */

import { useState, useCallback, useEffect } from 'react';
import { Input, List, Typography, Spin, Empty, Tag, Space, Alert } from 'antd';
import { SearchOutlined, EnvironmentOutlined, CheckOutlined, GlobalOutlined } from '@ant-design/icons';
import type { City } from '@/types/grid';
import { useDebounce } from '@/hooks/useDebounce';
import { searchLocations, type GeocodingResult } from '@/services/geocodingApi';

const { Text } = Typography;

// Popular cities with pre-defined bounds (shown when no search query)
const POPULAR_CITIES: City[] = [
  {
    name: 'Berlin',
    country: 'Germany',
    bounds: { north: 52.6755, south: 52.3382, east: 13.7611, west: 13.0884 },
    center: { lat: 52.52, lng: 13.405 },
  },
  {
    name: 'London',
    country: 'United Kingdom',
    bounds: { north: 51.6919, south: 51.2867, east: 0.3340, west: -0.5103 },
    center: { lat: 51.5074, lng: -0.1278 },
  },
  {
    name: 'Paris',
    country: 'France',
    bounds: { north: 48.9022, south: 48.8156, east: 2.4699, west: 2.2241 },
    center: { lat: 48.8566, lng: 2.3522 },
  },
  {
    name: 'New York',
    country: 'United States',
    bounds: { north: 40.9176, south: 40.4774, east: -73.7004, west: -74.2591 },
    center: { lat: 40.7128, lng: -74.006 },
  },
  {
    name: 'Tokyo',
    country: 'Japan',
    bounds: { north: 35.8984, south: 35.5189, east: 139.9100, west: 139.5600 },
    center: { lat: 35.6762, lng: 139.6503 },
  },
  {
    name: 'Amsterdam',
    country: 'Netherlands',
    bounds: { north: 52.4312, south: 52.2783, east: 5.0179, west: 4.7288 },
    center: { lat: 52.3676, lng: 4.9041 },
  },
];

interface CitySearchProps {
  onSelect: (city: City) => void;
  selectedCity?: City;
}

/**
 * Convert geocoding result to City format
 */
function geocodingResultToCity(result: GeocodingResult): City {
  return {
    name: result.name,
    country: result.country,
    bounds: result.bounds,
    center: result.center,
  };
}

export function CitySearch({ onSelect, selectedCity }: CitySearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce the search query
  const debouncedQuery = useDebounce(searchQuery, 300);

  // Perform geocoding search when debounced query changes
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedQuery || debouncedQuery.trim().length < 2) {
        setSearchResults([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await searchLocations(debouncedQuery, 10);
        setSearchResults(results);
      } catch (err) {
        console.error('Geocoding search failed:', err);
        setError('Search failed. Please try again.');
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery]);

  const handleCitySelect = useCallback(
    (city: City) => {
      onSelect(city);
      setSearchQuery(city.name);
    },
    [onSelect]
  );

  const handleResultSelect = useCallback(
    (result: GeocodingResult) => {
      const city = geocodingResultToCity(result);
      onSelect(city);
      setSearchQuery(result.name);
    },
    [onSelect]
  );

  const isSelected = (city: City) =>
    selectedCity?.name === city.name && selectedCity?.country === city.country;

  const isResultSelected = (result: GeocodingResult) =>
    selectedCity?.name === result.name && selectedCity?.country === result.country;

  // Show search results when searching, otherwise show popular cities
  const showSearchResults = searchQuery.trim().length >= 2;

  return (
    <div>
      {/* Search input */}
      <Input
        size="large"
        prefix={<SearchOutlined />}
        placeholder="Search any city, region, or country..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ marginBottom: 16 }}
        allowClear
        onClear={() => {
          setSearchQuery('');
          setSearchResults([]);
        }}
      />

      {/* Error message */}
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Popular cities (shown when not searching) */}
      {!showSearchResults && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Popular cities:
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {POPULAR_CITIES.map((city) => (
              <Tag
                key={`${city.name}-${city.country}`}
                color={isSelected(city) ? 'blue' : 'default'}
                onClick={() => handleCitySelect(city)}
                style={{ cursor: 'pointer', padding: '4px 12px' }}
                icon={isSelected(city) ? <CheckOutlined /> : undefined}
              >
                {city.name}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Search results / city list */}
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin tip="Searching..." />
          </div>
        ) : showSearchResults ? (
          // Show geocoding search results
          searchResults.length === 0 ? (
            <Empty
              description={
                <span>
                  No locations found for "{searchQuery}"
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Try a different search term
                  </Text>
                </span>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              size="small"
              dataSource={searchResults}
              renderItem={(result) => (
                <List.Item
                  onClick={() => handleResultSelect(result)}
                  style={{
                    cursor: 'pointer',
                    padding: '12px 16px',
                    background: isResultSelected(result) ? 'var(--color-primary-bg)' : undefined,
                    borderRadius: 8,
                    marginBottom: 4,
                    transition: 'background 0.2s',
                  }}
                  className="city-search-item"
                >
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <GlobalOutlined
                        style={{
                          color: isResultSelected(result)
                            ? 'var(--color-primary)'
                            : 'var(--color-text-tertiary)',
                        }}
                      />
                      <div>
                        <Text
                          strong
                          style={{
                            color: isResultSelected(result) ? 'var(--color-primary)' : undefined,
                          }}
                        >
                          {result.name}
                        </Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          {result.country}
                        </Text>
                        <br />
                        <Text
                          type="secondary"
                          style={{ fontSize: 11 }}
                          ellipsis={{ tooltip: result.displayName }}
                        >
                          {result.type} • {result.displayName.slice(0, 60)}
                          {result.displayName.length > 60 ? '...' : ''}
                        </Text>
                      </div>
                    </Space>
                    {isResultSelected(result) && (
                      <CheckOutlined style={{ color: 'var(--color-primary)' }} />
                    )}
                  </Space>
                </List.Item>
              )}
            />
          )
        ) : (
          // Show popular cities list when not searching
          <List
            size="small"
            dataSource={POPULAR_CITIES}
            renderItem={(city) => (
              <List.Item
                onClick={() => handleCitySelect(city)}
                style={{
                  cursor: 'pointer',
                  padding: '12px 16px',
                  background: isSelected(city) ? 'var(--color-primary-bg)' : undefined,
                  borderRadius: 8,
                  marginBottom: 4,
                }}
              >
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <EnvironmentOutlined
                      style={{
                        color: isSelected(city)
                          ? 'var(--color-primary)'
                          : 'var(--color-text-tertiary)',
                      }}
                    />
                    <div>
                      <Text
                        strong
                        style={{ color: isSelected(city) ? 'var(--color-primary)' : undefined }}
                      >
                        {city.name}
                      </Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        {city.country}
                      </Text>
                    </div>
                  </Space>
                  {isSelected(city) && <CheckOutlined style={{ color: 'var(--color-primary)' }} />}
                </Space>
              </List.Item>
            )}
          />
        )}
      </div>

      {/* Selected city info */}
      {selectedCity && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: 'var(--color-bg-spotlight)',
            borderRadius: 8,
          }}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Selected area:
          </Text>
          <Text strong>
            {selectedCity.name}, {selectedCity.country}
          </Text>
        </div>
      )}
    </div>
  );
}

export default CitySearch;
